/**
 * Keyboard input mapping — browser KeyboardEvent → terminal byte sequences.
 *
 * This module is browser-specific (uses KeyboardEvent types) but is
 * decoupled from the terminal state machine and boot logic so keyboard
 * mapping can be tested and reused independently.
 */

export const KEY_EVENT_SIZE = 16;

export const KEY_PRESSED = 1;
export const KEY_RELEASED = 2;
export const KEY_REPEAT = 4;

export const MOD_SHIFT = 1;
export const MOD_CTRL = 2;
export const MOD_ALT = 4;
export const MOD_META = 8;

export const HID_KEYCODE_BY_CODE: Record<string, number> = {
  KeyA: 0x04, KeyB: 0x05, KeyC: 0x06, KeyD: 0x07, KeyE: 0x08, KeyF: 0x09, KeyG: 0x0A,
  KeyH: 0x0B, KeyI: 0x0C, KeyJ: 0x0D, KeyK: 0x0E, KeyL: 0x0F, KeyM: 0x10, KeyN: 0x11,
  KeyO: 0x12, KeyP: 0x13, KeyQ: 0x14, KeyR: 0x15, KeyS: 0x16, KeyT: 0x17, KeyU: 0x18,
  KeyV: 0x19, KeyW: 0x1A, KeyX: 0x1B, KeyY: 0x1C, KeyZ: 0x1D,
  Digit1: 0x1E, Digit2: 0x1F, Digit3: 0x20, Digit4: 0x21, Digit5: 0x22,
  Digit6: 0x23, Digit7: 0x24, Digit8: 0x25, Digit9: 0x26, Digit0: 0x27,
  Enter: 0x28, NumpadEnter: 0x58, Escape: 0x29, Backspace: 0x2A, Tab: 0x2B, Space: 0x2C,
  Minus: 0x2D, Equal: 0x2E, BracketLeft: 0x2F, BracketRight: 0x30, Backslash: 0x31,
  Semicolon: 0x33, Quote: 0x34, Backquote: 0x35, Comma: 0x36, Period: 0x37, Slash: 0x38,
  CapsLock: 0x39,
  F1: 0x3A, F2: 0x3B, F3: 0x3C, F4: 0x3D, F5: 0x3E, F6: 0x3F,
  F7: 0x40, F8: 0x41, F9: 0x42, F10: 0x43, F11: 0x44, F12: 0x45,
  PrintScreen: 0x46, ScrollLock: 0x47, Pause: 0x48,
  Insert: 0x49, Home: 0x4A, PageUp: 0x4B, Delete: 0x4C, End: 0x4D, PageDown: 0x4E,
  ArrowRight: 0x4F, ArrowLeft: 0x50, ArrowDown: 0x51, ArrowUp: 0x52,
  NumLock: 0x53, NumpadDivide: 0x54, NumpadMultiply: 0x55, NumpadSubtract: 0x56, NumpadAdd: 0x57,
  Numpad1: 0x59, Numpad2: 0x5A, Numpad3: 0x5B, Numpad4: 0x5C, Numpad5: 0x5D,
  Numpad6: 0x5E, Numpad7: 0x5F, Numpad8: 0x60, Numpad9: 0x61, Numpad0: 0x62, NumpadDecimal: 0x63,
  IntlBackslash: 0x64, ContextMenu: 0x65,
  ControlLeft: 0xE0, ShiftLeft: 0xE1, AltLeft: 0xE2, MetaLeft: 0xE3, OSLeft: 0xE3,
  ControlRight: 0xE4, ShiftRight: 0xE5, AltRight: 0xE6, MetaRight: 0xE7, OSRight: 0xE7,
};


export function keyboardModifiers(e: KeyboardEvent): number {
  let bits = 0;
  if (e.shiftKey) bits |= MOD_SHIFT;
  if (e.ctrlKey) bits |= MOD_CTRL;
  if (e.altKey) bits |= MOD_ALT;
  if (e.metaKey) bits |= MOD_META;
  return bits;
}

export function isAltGraphActive(e: KeyboardEvent): boolean {
  return !!e.getModifierState("AltGraph");
}

export function isDeadOrComposingKey(e: KeyboardEvent): boolean {
  const legacyCode = Number((e as unknown as { keyCode?: number }).keyCode ?? 0);
  return e.isComposing || e.key === "Dead" || legacyCode === 229;
}

export function keycodeFromDomEvent(e: KeyboardEvent): number {
  const hid = HID_KEYCODE_BY_CODE[e.code];
  if (typeof hid === "number") return hid;
  const legacy = Number((e as unknown as { keyCode?: number }).keyCode ?? 0);
  if (Number.isFinite(legacy) && legacy > 0 && legacy <= 0xFFFF) return legacy;
  return 0;
}

export function unicodeFromDomEvent(e: KeyboardEvent): number {
  if (isDeadOrComposingKey(e) || e.key.length !== 1) return 0;
  const cp = e.key.codePointAt(0);
  if (typeof cp !== "number" || cp < 0 || cp > 0xFFFF) return 0;
  return cp;
}

export function packKeyboardEvent(e: KeyboardEvent, kind: "down" | "up"): Uint8Array {
  const out = new Uint8Array(KEY_EVENT_SIZE);
  const view = new DataView(out.buffer);
  view.setBigUint64(0, BigInt(Math.floor(performance.now() * 1_000_000)), true);
  view.setUint16(8, keycodeFromDomEvent(e), true);
  view.setUint16(10, kind === "down" ? unicodeFromDomEvent(e) : 0, true);
  let flags = kind === "down" ? KEY_PRESSED : KEY_RELEASED;
  if (kind === "down" && e.repeat) flags |= KEY_REPEAT;
  view.setUint8(12, flags);
  view.setUint8(13, keyboardModifiers(e));
  view.setUint16(14, 0, true);
  return out;
}

export function keyToConsoleInput(e: KeyboardEvent): string {
  if (isDeadOrComposingKey(e)) {
    return "";
  }

  switch (e.key) {
    case "Enter": return "\r";
    case "Backspace": return "\x08";
    case "Tab": return "\t";
    case "Escape": return "\x1b";
    case "ArrowUp": return "\x1b[A";
    case "ArrowDown": return "\x1b[B";
    case "ArrowRight": return "\x1b[C";
    case "ArrowLeft": return "\x1b[D";
    case "Home": return "\x1b[H";
    case "End": return "\x1b[F";
    case "Insert": return "\x1b[2~";
    case "Delete": return "\x1b[3~";
    case "PageUp": return "\x1b[5~";
    case "PageDown": return "\x1b[6~";
    case "F1": return "\x1bOP";
    case "F2": return "\x1bOQ";
    case "F3": return "\x1bOR";
    case "F4": return "\x1bOS";
    case "F5": return "\x1b[15~";
    case "F6": return "\x1b[17~";
    case "F7": return "\x1b[18~";
    case "F8": return "\x1b[19~";
    case "F9": return "\x1b[20~";
    case "F10": return "\x1b[21~";
    case "F11": return "\x1b[23~";
    case "F12": return "\x1b[24~";
    default:
      break;
  }

  const altGraph = isAltGraphActive(e);

  if (altGraph && !e.metaKey && e.key.length === 1) {
    return e.key;
  }

  if (e.ctrlKey && !e.altKey && !e.metaKey && !altGraph) {
    if (e.key.length === 1) {
      const ch = e.key.toUpperCase();
      const code = ch.charCodeAt(0);
      if (code >= 65 && code <= 90) {
        return String.fromCharCode(code - 64);
      }
      switch (ch) {
        case "@":
        case " ":
          return "\x00";
        case "[":
          return "\x1b";
        case "\\":
          return "\x1c";
        case "]":
          return "\x1d";
        case "^":
          return "\x1e";
        case "_":
          return "\x1f";
        case "?":
          return "\x7f";
        default:
          break;
      }
    }
    return "";
  }

  if (e.altKey && !e.ctrlKey && !e.metaKey && !altGraph && e.key.length === 1) {
    return `\x1b${e.key}`;
  }

  if (!e.ctrlKey && !e.metaKey && e.key.length === 1) {
    return e.key;
  }

  return "";
}

